import { db } from './firebase.js';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  getOwnedCardIds,
  migrateFromLegacyCollection,
  getCollectionStats
} from './collectionService.ts';

const COLLECTION_DOC_ID = 'collection';
const LEGACY_DOC_ID = 'collection_legacy';

function setToArray(setLike: Set<string> | string[] | null | undefined): string[] {
  return Array.isArray(setLike) ? setLike : Array.from(setLike || []);
}

function arrayToSet(arr: string[] | null | undefined): Set<string> {
  return new Set(Array.isArray(arr) ? arr : []);
}

/**
 * Load collection from new instance-based subcollection
 * Falls back to legacy format if needed
 */
export async function loadRemoteCollection(userId) {
  if (!db || !userId) return null;

  try {
    // First try to load from new instance-based collection
    const ownedIds = await getOwnedCardIds(userId);

    // Also load wanted list from legacy location (wanted stays as simple list)
    const wantedRef = doc(db, 'users', userId, 'app', COLLECTION_DOC_ID);
    const wantedSnap = await getDoc(wantedRef);
    const wantedData = wantedSnap.exists() ? wantedSnap.data() : {};

    if (ownedIds.size > 0) {
      return {
        owned: ownedIds,
        wanted: arrayToSet(wantedData.wanted || []),
        updatedAt: wantedData.updatedAt || null,
        isNewFormat: true
      };
    }

    // Fall back to legacy format
    const legacyRef = doc(db, 'users', userId, 'app', COLLECTION_DOC_ID);
    const snap = await getDoc(legacyRef);
    if (!snap.exists()) return null;

    const data = snap.data() || {};
    return {
      owned: arrayToSet(data.owned || []),
      wanted: arrayToSet(data.wanted || []),
      updatedAt: data.updatedAt || null,
      isNewFormat: false
    };
  } catch (error) {
    console.error('Error loading collection:', error);
    return null;
  }
}

/**
 * Save collection - migrates to new format if not already migrated
 */
export async function saveRemoteCollection(userId, ownedSet, wantedSet) {
  if (!db || !userId) return;

  const owned = setToArray(ownedSet);
  const wanted = setToArray(wantedSet);

  try {
    // Check if we need to migrate to new format
    const existingIds = await getOwnedCardIds(userId);

    if (existingIds.size === 0 && owned.length > 0) {
      // First time saving with new system - migrate
      await migrateFromLegacyCollection(userId, owned);
    }

    // Save wanted list (stays as simple array for now)
    const wantedRef = doc(db, 'users', userId, 'app', COLLECTION_DOC_ID);
    await setDoc(
      wantedRef,
      { wanted, updatedAt: serverTimestamp() },
      { merge: true }
    );

    // Update profile stats
    const stats = await getCollectionStats(userId);
    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      {
        stats: {
          owned: stats.uniqueCards,
          totalCards: stats.totalCards,
          wanted: wanted.length,
          cardsForTrade: stats.cardsForTrade
        },
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error saving collection:', error);

    // Fallback to legacy save
    const ref = doc(db, 'users', userId, 'app', COLLECTION_DOC_ID);
    await setDoc(
      ref,
      { owned, wanted, updatedAt: serverTimestamp() },
      { merge: true }
    );

    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      { stats: { owned: owned.length, wanted: wanted.length }, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
}

/**
 * Merge local and remote collections
 */
export function mergeCollections(localOwned, localWanted, remoteOwned, remoteWanted) {
  const owned = new Set([...(localOwned || []), ...(remoteOwned || [])]);
  const wanted = new Set([...(localWanted || []), ...(remoteWanted || [])]);
  // Ensure disjoint: owned wins over wanted
  for (const key of owned) {
    if (wanted.has(key)) wanted.delete(key);
  }
  return { owned, wanted };
}

/**
 * Migrate legacy collection to new instance-based format
 */
export async function migrateLegacyCollection(userId) {
  if (!db || !userId) return { migrated: 0, error: null };

  try {
    const legacyRef = doc(db, 'users', userId, 'app', COLLECTION_DOC_ID);
    const snap = await getDoc(legacyRef);

    if (!snap.exists()) return { migrated: 0, error: null };

    const data = snap.data() || {};
    const legacyOwned = data.owned || [];

    if (legacyOwned.length === 0) return { migrated: 0, error: null };

    const migrated = await migrateFromLegacyCollection(userId, legacyOwned);

    // Backup legacy data before removing
    if (migrated > 0) {
      const backupRef = doc(db, 'users', userId, 'app', LEGACY_DOC_ID);
      await setDoc(backupRef, {
        owned: legacyOwned,
        wanted: data.wanted || [],
        migratedAt: serverTimestamp()
      });
    }

    return { migrated, error: null };
  } catch (error) {
    console.error('Migration error:', error);
    return { migrated: 0, error: error.message };
  }
}


